import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, OnInit, ViewChild, AfterContentInit } from '@angular/core';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { ActivatedRoute, Router } from '@angular/router';
import { TransformType } from 'src/app/domainObjects/transformType';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { TransformerViewModel } from 'src/app/views/transformer-list/transformer-list.component';
import { ConfigViewModelService } from '../../../services/configViewModel/config-view-model.service';
import { IngestConfigViewModel } from '../../import-data/import-data.component';
import { DataTransformConfigViewModel } from '../../../viewModels/dataTransformConfigViewModel';
import { EventBrokerService, EventData } from 'src/app/services/eventBroker/event-broker.service';
import { Subscription } from 'rxjs';
import { SelectedIngestFarmChangedEvent } from '../../dataTransformConfig/ingest/ingest-work-flow-layout/ingest-work-flow-layout.component';
import { SelectedTabChangedEvent } from '../config-detail/config-detail.component';
import { UserDataService } from 'src/app/services/user-data-service';

export interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
}

enum NodeIdentity {
  root = 'root',
  subtasks = 'subTasks',
  tasks = 'tasks',
  activities = 'activities',
  ingest = 'ingest'
}

interface ConfigItemNode {
  id?: string;
  expandable: boolean;
  name: string;
  data?: any;
  iconColorClass?: string;
  isSelected: boolean;
  level: number;
}

class ConfigDocumentItem {
  id: string;
  isGroup = false;
  name: string;
  type: string;
  children?: ConfigDocumentItem[];
  data?: any;
  level: number;
  iconColorClass?: string;
  isSelected = false;

  constructor(name: string, children?: ConfigDocumentItem[], data?: any, iconColorClass?: string, id?: string) {
    this.id = id;
    this.type = '';
    this.name = name;
    this.children = children;
    this.data = data;
    this.iconColorClass = iconColorClass;
    this.isSelected = false;

    if (children) {
      this.isGroup = children.length > 0;
    }
  }
}

@Component({
    selector: 'app-config-document-tree',
    templateUrl: './config-document-tree.component.html',
    styleUrls: ['./config-document-tree.component.css'],
    standalone: false
})
export class ConfigDocumentTreeComponent implements OnInit, AfterContentInit {

  private configSub: Subscription;
  private eventBusSubscription: Subscription;

  constructor(
    private configViewModelService: ConfigViewModelService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private eventBroker: EventBrokerService,private userData: UserDataService) {
      // nothing to do...
  }

  @ViewChild('tree') tree;

  treeControl = new FlatTreeControl<ConfigItemNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    (node: ConfigDocumentItem, nodeLevel: number) => {
      return {

        id: node.id,
        expandable: node.isGroup,
        name: node.name,
        data: node.data,
        iconColorClass: node.iconColorClass ?? 'yellow-icon',
        isSelected: node.isSelected,
        level: nodeLevel,
      };
    }, node => node.level, node => node.expandable, node => node.children);

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  documentItems: ConfigDocumentItem[];
  lastSelectedNode?: any;

  configViewModelObserver = {
    next: (x: DataTransformConfigViewModel) => {
      //console.log('DataTransformConfigViewModel Observer got a next value: ' + x);

      this.bindTree(x);
      this.treeControl.expandAll();

      if (this.lastSelectedNode) {
        this.lastSelectedNode.isSelected = false;
      }
      if (x.activeTransformGuid) {
        this.lastSelectedNode = this.findNodeById(x.activeTransformGuid);
        if (this.lastSelectedNode) {
          this.lastSelectedNode.isSelected = true;
        }
      }
    },
    error: (err: Error) => console.error('Observer got an error: ' + err),
    complete: () => console.log('Observer got a complete notification'),
  };

  eventBusObserver = {
    next: (x: EventData) => {
      try {
        //console.log('EventBus Observer got a next value: ' + x);
        if (x instanceof SelectedIngestFarmChangedEvent) {
          const id = (x as SelectedIngestFarmChangedEvent).ingestFarmId;
          if (this.lastSelectedNode) {
            this.lastSelectedNode.isSelected = false;
          }
          if (id) {
            this.lastSelectedNode = this.findNodeById(id);
            if (this.lastSelectedNode) {
              this.lastSelectedNode.isSelected = true;
            }
          }
        }
      }
      catch (err) {
        //console.log(err);
      }
    },
    error: (err: Error) => console.error('Observer got an error: ' + err),
    complete: () => console.log('Observer got a complete notification'),
  };

  hasChild = (_: number, node: FlatNode) => node.expandable;
  refineryName: string = '';
  refineryConfigGuid: string = '';
  ngOnInit(): void {
    this.configSub = this.configViewModelService.configViewModel$.subscribe(this.configViewModelObserver);
    this.eventBusSubscription = this.eventBroker.eventBus.subscribe(this.eventBusObserver);
    const state = history.state;
    this.refineryName =state.refineryName;
    this.refineryConfigGuid=state.refineryConfigGuid
  }

  ngAfterContentInit(): void {
    // nothing to do...
  }

  private findNodeById(id: string): ConfigItemNode {
    return this.treeControl.dataNodes.find(d => d.id === id);
  }

  private bindTree(configViewModel: DataTransformConfigViewModel): void {
    const subtaskTransformItems = configViewModel.subTaskTransforms.transforms
      .map(t => new ConfigDocumentItem(t.name, null, t, 'subTask-color', t.guid));
    const taskTransformItems = configViewModel.taskTransforms.transforms
      .map(t => new ConfigDocumentItem(t.name, null, t, 'task-color', t.guid));
    const activityTransformItems = configViewModel.activityTransforms.transforms
      .map(t => new ConfigDocumentItem(t.name, null, t, 'activity-color', t.guid));

  
    this.documentItems = [
      new ConfigDocumentItem(configViewModel.name, [
        new ConfigDocumentItem('Ingest', [
          new ConfigDocumentItem('Data Farms', configViewModel.ingestConfigs
            .map(t => new ConfigDocumentItem( t.name, null, t, 'ingest-color', t.guid)), null, 'ingest-color-dark'),
        ], null, 'ingest-color-dark', NodeIdentity.ingest),
        new ConfigDocumentItem('Subtask', [
          new ConfigDocumentItem('Transforms',
            subtaskTransformItems.length === 0 ? [
              new ConfigDocumentItem('No Transforms', null, null, 'gray-icon')
            ] : subtaskTransformItems, null, 'subTask-color-dark'),
        ], null, 'subTask-color-dark', NodeIdentity.subtasks),
        new ConfigDocumentItem('Task', [
          new ConfigDocumentItem('Transforms',
            taskTransformItems.length === 0 ? [
              new ConfigDocumentItem('No Transforms', null, null, 'gray-icon')
            ] : taskTransformItems, null, 'task-color-dark'),
        ], null, 'task-color-dark', NodeIdentity.tasks),
        new ConfigDocumentItem('Activity', [
          new ConfigDocumentItem('Transforms',
            activityTransformItems.length === 0 ? [
              new ConfigDocumentItem('No Transforms', null, null, 'gray-icon')
            ] : activityTransformItems, null, 'activity-color-dark'),
        ], null, 'activity-color-dark', NodeIdentity.activities),
      ], null, null, NodeIdentity.root)
    ];
  /* New Menu with only Transformers Left menu Item
    this.documentItems = [
      new ConfigDocumentItem(configViewModel.name, [
        new ConfigDocumentItem('Transforms',
          subtaskTransformItems.length === 0 ? [
            new ConfigDocumentItem('No Transforms', null, null, 'gray-icon')
          ] : subtaskTransformItems, null, 'subTask-color-dark'),
        
      ], null, null, NodeIdentity.root)
    ];
  */
    this.dataSource.data = this.documentItems;
  }

  async onNodeClicked(item: any): Promise<void> {
    let isSelectable = false;

    if (item.data instanceof TransformerViewModel) {
      this.updateSelectState(item, true);
      this.showTransformDetail(item.data);
    } else if (item.id === NodeIdentity.root || item.data instanceof IngestConfigViewModel) {
      // Since this action reloads the config detail and creates a new Config ViewModel
      // we want to unsubscribe so we don't end up unnecessarily rebinding the tree
      this.configSub.unsubscribe();

      try {
        isSelectable = item.data instanceof IngestConfigViewModel;
        this.updateSelectState(item, isSelectable);

        const config = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig;
        const configId = config?.guid;
        const ingestVm: IngestConfigViewModel = item.data as IngestConfigViewModel;

        if (item.data instanceof IngestConfigViewModel) {
          this.router.navigate(
            [`dataTransformConfig/${config.guid}/configDetail`],
              { queryParams: {activeFarmId: ingestVm.guid},
                state: {config, selectedTabIndex: 0}
              });
        } else {
          this.router.navigateByUrl(`dataTransformConfig/${config.guid}/configDetail`,
            {state: {config, selectedTabIndex: 0, activeFarmId: item.data.guid}});
        }
      } finally {
        // wait for the config to load before re-subscribe to changes
        await this.delay(1000);
        this.configSub = this.configViewModelService.configViewModel$.subscribe(this.configViewModelObserver);
      }
    } else if (item.id === NodeIdentity.subtasks) {
      this.updateSelectState(item, true);
      const config = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig;
      this.router.navigate(
        [`dataTransformConfig/${config.guid}/configDetail`],
          { queryParams: {selectedTabIndex: 1},
            state: {config, selectedTabIndex: 1}
          });
      this.eventBroker.sendEvent( new SelectedTabChangedEvent(1) );
    } else if (item.id === NodeIdentity.tasks) {

      this.updateSelectState(item, true);
      const config = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig;
      this.router.navigate(
        [`dataTransformConfig/${config.guid}/configDetail`],
          { queryParams: {selectedTabIndex: 2},
            state: {config, selectedTabIndex: 2}
          });
      this.eventBroker.sendEvent( new SelectedTabChangedEvent(2) );
    } else if (item.id === NodeIdentity.activities) {
      this.updateSelectState(item, true);
      const config = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig;
      this.router.navigate(
        [`dataTransformConfig/${config.guid}/configDetail`],
          { queryParams: {selectedTabIndex: 3},
            state: {config, selectedTabIndex: 3}
          });
      this.eventBroker.sendEvent( new SelectedTabChangedEvent(3) );
    } else if (item.id === NodeIdentity.ingest) {
      this.updateSelectState(item, true);
      const config = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig;
      this.router.navigate(
        [`dataTransformConfig/${config.guid}/configDetail`],
          { queryParams: {selectedTabIndex: 0},
            state: {config, selectedTabIndex: 0}
          });
      this.eventBroker.sendEvent( new SelectedTabChangedEvent(0) );
    }

  }

  private updateSelectState(item: any, isSelectable: boolean): void {

    if (isSelectable) {
      if (this.lastSelectedNode) {
        this.lastSelectedNode.isSelected = false;
      }

      item.isSelected = true;
      this.lastSelectedNode = item;
    }
  }

  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  showTransformDetail(transform: TransformerViewModel): void {
    // this.navigationService.openSideDrawer();
    // this.router.navigate(['./', { outlets: {sidebar: 'extractorTransformDetail'}}]);

    //console.log(`show transform detail: ${transform.type}`);
    //console.log(transform);

    const configId = this.configViewModelService.getDataTransformConfigViewModel().dataTransformerConfig?.guid;

    switch (transform.type) {
      case TransformType.rowExtractor:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/extractorTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.activityNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/activityNamingTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.eventGroupNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/eventGroupNamerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.eventNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/eventNamerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.groupAttributor:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/groupAttributorTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.columnCalc:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/columnCalcTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.columnLookup:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/columnLookupTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.rowFiller:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/rowFillerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.anonymizer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/anonymizerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
      case TransformType.columnCleaner:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/columnCleanerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
    }
  }
  hideshowrow(transform:any):boolean{
    var currentNodeID=this.userData.getSelectedNodeDetails();
    var hs:boolean=true;
    if(transform.data!=null){
      hs=(transform.data.nodeID!=currentNodeID.id);
    }
    return hs;//(transform.transform.nodeID!=currentNodeID.id);
  }
}
